import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { firstValueFrom, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { SnackService } from "../../../shared/services/snack-service.service";
import { TypeDateLimite } from "../../articles/enums/type-date-limite.enum";
import { PrepreparationLigne } from "../models/prepreparation-ligne.model";
import { printPdf } from "../../../shared/utils/print-pdf.utils";

/** P5c — Ligne d'agrégat par commande pour la page de pilotage. */
export interface PilotageCommandeRow {
  commandeClientId: string;
  numero: string;
  numeroCommandeClient: string | null;
  tiersId: string;
  tiersDenomination: string | null;
  etat: string;
  dateLivraisonPrevue: string | null;
  dateExpeditionPrevue: string | null;
  slotHoraireEnlevement: string | null;
  nbLignes: number;
  nbLignesAvecPrep: number;
  nbLignesValidees: number;
  nbLignesARevoir: number;
  nbLignesAlerteDDM: number;
  couverturePct: number;
}

/** P5c — Pivot par produit : agrégat besoins / stock dispo / couverture. */
export interface PilotageProduitGridResponse {
  article: {
    id: string;
    denomination: string | null;
    code: string | null;
    uniteArticle: string | null;
    typeDateLimite?: 'DDM' | 'DLC' | 'AUCUNE' | 'MIXTE';
    withLot?: boolean;
    isPrestation?: boolean;
  };
  /** 1er OF actif (bandeau « Production en attente » au-dessus du grid). */
  nextOf?: {
    numero: string;
    dateDebutPrevue: string | null;
    qtyRestante: number;
  } | null;
  lots: {
    id: string;
    numero: string;
    dluo: string | null;
    typeDateLimite: string;
    /** Date d'entrée du lot (FIFO sans DDM). */
    dateCreation?: string | null;
    stockPhysique: number;
    stockReserveAutreFlux: number;
    stockDispoTotal: number;
    entrepotIds: string[];
    entrepots: { id: string; denomination: string }[];
    /** 2026-06-01 — Ventilage du stock consommable par entrepôt racine
     *  (inclut l'engagé réinjecté pour l'affichage des lignes engagées). */
    stockParEntrepot: { entrepotId: string; qty: number }[];
    /** 2026-06-12 — Stock LIBRE par entrepôt (hors engagé) — base pour
     *  l'allocation et le nettoyage cross-entrepôt. */
    stockLibreParEntrepot?: { entrepotId: string; qty: number }[];
  }[];
  commandes: {
    ligneId: string;
    ccId: string;
    ccNumero: string;
    tiersDenomination: string | null;
    besoinUV: number;
    besoinUG: number;
    uniteVente: string | null;
    coefficient: number;
    /** 2026-06-01 — Entrepôts assignés à la CC. Vide = mode global. */
    entrepotsAssignes: string[];
    dateExpe: string | null;
    dateLivraison: string | null;
    /** Date de référence effective pour les calculs DDM/DLC (today si livraison en retard). */
    dateExpeEffective: string | null;
    dateLivraisonEffective: string | null;
    /** True si la livraison contractuelle est dans le passé (les calculs sont rebasés sur aujourd'hui). */
    livraisonEnRetard: boolean;
    /** Nombre de jours de retard (positif si en retard, 0 sinon). */
    joursRetard: number;
    ddmMini: { jours: number | null; niveauSource: string | null };
    etatPrep: 'aucun' | 'simulation' | 'engagee' | 'consommee';
    verrouillee: boolean;
  }[];
  cells: {
    ligneId: string;
    lotId: string;
    qtyAffectee: number;
    statut:
      | 'OK'
      | 'FORCE'
      | 'DEPASSEE'
      | 'HORS_ENTREPOT'
      | 'AUCUNE'
      | 'DATE_MANQUANTE';
    raisonNonEligible?: string;
    marge?: number;
    motifForce?: string | null;
    motifForceCommentaire?: string | null;
    alerteDDM?: boolean;
    engagee?: boolean;
  }[];
  /** Stats agrégées pour le header de la fiche produit. */
  stats?: {
    besoinTotalUG: number;
    besoinTotalUV: number;
    stockDispoTotal: number;
    /**
     * 2026-06-01 — Stock dispo restreint aux lots éligibles aux CCs
     * visibles (DDM/DLC respectées + entrepôts assignés). Sert au
     * calcul du KPI "Manquant" cohérent avec la grille.
     */
    stockDispoEligible: number;
    manquant: number;
    qtyEnOF: number;
    nbCommandes: number;
    nbLignes: number;
    coefficient: number;
    uniteVente: string | null;
    premierDepart: string | null;
  };
}

export interface StockForecastEvent {
  date: string;
  type: "DEPART_CC" | "OF_PROD" | "RECEPT_FOURNI";
  qtyUG: number;
  label: string;
  refId: string;
}

export interface StockForecastResponse {
  article: {
    id: string;
    denomination: string | null;
    uniteArticle: string | null;
  };
  horizonJours: number;
  stockInitial: number;
  stockFinal: number;
  minStock: number;
  events: StockForecastEvent[];
  dataPoints: { date: string; stock: number; rupture: boolean }[];
}

export interface PilotageProduitRow {
  articleId: string;
  articleDenomination: string | null;
  /** Code article (back 2026-06-15) — affiché sur la card, cherchable. */
  reference?: string | null;
  besoinTotal: number;
  /**
   * Besoin en UG EXACT (Σ qty×coef par ligne, back 2026-07-03). Préférer à
   * `besoinTotal × coefficient` (faux si coefficients hétérogènes).
   */
  besoinUG?: number;
  qtyReservee: number;
  /**
   * 30/05/2026 — Qty physiquement engagée en zone-prepa-CC pour cet
   * article (CCs de l'horizon). Permet de distinguer "tout engagé"
   * vs "couvert à engager" dans le KPI badge.
   */
  qtyEngageePhysique: number;
  stockPhysique: number;
  stockDispo: number;
  /**
   * 30/05/2026 — Stock effectivement allouable (lots LIBRE, hors
   * zone-prepa, DLUO ≥ livraison contractuelle d'au moins une CC, et
   * entrepôt assigné à au moins une CC). Détecte le cas "Couvert
   * sur le papier mais aucun lot éligible".
   */
  stockDispoEligible: number;
  /** Qty restante à produire sur OF actifs (UG). */
  qtyEnOF: number;
  couverturePct: number;
  nbCommandes: number;
  nbLignes: number;
  nbLignesARevoir: number;
  premierDeparture: string | null;
  uniteVente: string | null;
  uniteArticle: string | null;
  coefficient: number;
  /** Numéros des CC consommant ce produit (pour recherche par N°). */
  ccNumeros?: string[];
  /** Noms des tiers consommant ce produit (pour recherche par client). */
  tiersNames?: string[];
  /** Noms des groupes de tiers consommant ce produit (filtre par groupe). */
  tiersGroupNames?: string[];
  /** Type de date limite agrégé des lots actifs (DDM/DLC/AUCUNE/MIXTE). */
  typeDateLimite?: 'DDM' | 'DLC' | 'AUCUNE' | 'MIXTE';
  /** False si article non géré multi-lot (lot unique global). */
  withLot?: boolean;
  /** True si prestation (immatériel, sans stock physique). */
  isPrestation?: boolean;
  /** 1er OF actif (bandeau « Production en attente »). */
  nextOf?: {
    numero: string;
    dateDebutPrevue: string | null;
    qtyRestante: number;
  } | null;
}

/** P5d — Lot affecté (mini, pour drilldown). */
export interface DrilldownLot {
  lotId: string;
  numero: string;
  dluo: string | null;
  quantite: number;
  quantiteConsommee: number;
  alerteDDM: boolean;
  /** Code motif du forçage commercial (uniquement si alerteDDM=true). */
  motifForce: string | null;
  /** Commentaire libre (si motifForce=AUTRE). */
  motifForceCommentaire: string | null;
}

/** P5d — Drilldown commande : ligne d'une commande avec article + statut prep + lots. */
export interface PilotageCommandeDrilldownRow {
  ligneId: string;
  articleId: string;
  articleDenomination: string | null;
  /** Demande en UV (Carton...). */
  quantite: number;
  /** Demande en UG (= quantite × coefficient). */
  quantiteUG: number;
  /** Σ qty lots affectés (en UG, l'unité de stock). */
  quantiteAffectee: number;
  /** quantiteUG − quantiteAffectee (en UG). */
  quantiteRestante: number;
  uniteVente: string | null;
  uniteArticle: string | null;
  coefficient: number;
  dateExpe: string | null;
  statutPrep: string | null;
  aRevoir: boolean;
  prepLigneId: string | null;
  lots: DrilldownLot[];
}

/** P5d — Synthèse des réservations pré-prépa pour un lot. */
export interface LotReservationLigneRow {
  prepLigneId: string;
  ligneId: string;
  commandeClientId: string;
  numero: string;
  tiersDenomination: string | null;
  statut: string;
  aRevoir: boolean;
  quantite: number;
  quantiteConsommee: number;
  quantiteRestante: number;
}

export interface LotReservationsSummary {
  reserveValidee: number;
  reserveDraft: number;
  lignes: LotReservationLigneRow[];
}

/** P5d — Stock d'un lot pour la vue drilldown produit. */
export interface LotStockRow {
  lotId: string;
  numero: string;
  dluo: string | null;
  ofNumero: string | null;
  stockPhysique: number;
  reserveValidee: number;
  reserveDraft: number;
  stockDispo: number;
  /** R3 refonte DDM/DLC — type de date limite du lot. */
  typeDateLimite: TypeDateLimite;
  /** R3 — true si DLC dépassée (lot exclu du picking). */
  dlcDepassee: boolean;
}

/** P5d — Lot affichable dans le picker manuel d'affectation. */
export interface LotDisponibleRow {
  lotId: string;
  numero: string;
  dluo: string | null;
  ofNumero: string | null;
  stockPhysique: number;
  stockReserve: number;
  stockDispo: number;
  ddmOk: boolean;
  /** R3 refonte DDM/DLC — type de date limite du lot. */
  typeDateLimite: TypeDateLimite;
  /** R3 — true si DLC dépassée (lot exclu du picking). */
  dlcDepassee: boolean;
  /**
   * Refonte UX 2-colonnes (mai 2026) — catégorie sémantique pour code
   * couleur :
   *  - OK       : lot respecte la contrainte DLC mini (vert)
   *  - FORCE    : DLUO < DLC mini mais ≥ date expé (orange — motif requis)
   *  - DEPASSEE : DLUO < date expé ou DLC dépassée (rouge — bloque)
   *  - AUCUNE   : article sans contrainte de date (gris neutre)
   */
  categorieDDM: 'OK' | 'FORCE' | 'DEPASSEE' | 'AUCUNE';
  /** Qty déjà engagée pour cette CC sur ce lot (récupérable via re-allocation). */
  qtyEnZonePrepaCetteCC: number;
  /** Qty engagée pour d'autres CC (indisponible). */
  qtyEnZonePrepaAutreCC: number;
  /** Statut du lot (LIBRE / QUARANTAINE / BLOQUE) — mode diagnostic. */
  statutLot?: string;
  /** True si le lot est utilisable. */
  eligible?: boolean;
  /** Raison textuelle si non éligible (mode diagnostic). */
  raisonNonEligible?: string | null;
  /**
   * Refonte entrepôts (juin 2026) — true si le stock consommable du lot
   * n'est que dans des entrepôts non assignés à la CC (rejeté par l'auto).
   */
  horsEntrepot?: boolean;
  /**
   * Cohérence grille multicommandes (juin 2026) — qté de stock consommable
   * du lot située HORS des entrepôts assignés à la CC. > 0 = il existe du
   * stock ailleurs (info « +X dispo hors entrepôts assignés »).
   */
  stockHorsEntrepotAssigne?: number;
  /**
   * Liste des entrepôts où ce lot a du stock physique > 0. Affichée en
   * card lot quand le scope vaut "tous" pour informer l'opérateur de
   * la provenance du stock.
   */
  entrepots?: { id: string; denomination: string }[];
  /**
   * Fusion mai 2026 — Stocks vrac (paletteSSCC=null) actuellement en
   * zone-prepa-CC pour ce lot. Sert au front pour construire des
   * mouvements `PREPA_EXPEDITION` lors du clic "Sur palette" dans la
   * section "Lots engagés à palétiser" du fusion panel.
   */
  stocksEnZonePrepaCetteCC?: {
    id: string;
    adresseId: string;
    emplacementId?: string;
    quantite: number;
  }[];
}

/** P5d — Drilldown produit : commandes consommatrices d'un article. */
export interface PilotageProduitDrilldownRow {
  ligneId: string;
  commandeClientId: string;
  numero: string;
  tiersDenomination: string | null;
  quantite: number;
  quantiteUG: number;
  quantiteAffectee: number;
  quantiteRestante: number;
  uniteVente: string | null;
  uniteArticle: string | null;
  coefficient: number;
  dateExpe: string | null;
  statutPrep: string | null;
  aRevoir: boolean;
  prepLigneId: string | null;
  lots: DrilldownLot[];
}

/**
 * P4 — Service front pour les pré-préparations.
 *
 * Pas de pattern 3-file (api/store/facade) : les pré-prépas sont contextualisées
 * par commande, pas un store global. State signals locaux par-commande chargée.
 *
 * Endpoints consommés :
 *   GET  /prepreparation/commande/:commandeId
 *   GET  /prepreparation/ligne/:ligneId
 *   POST /prepreparation/auto-affecter/ligne/:ligneId
 *   POST /prepreparation/auto-affecter/commande/:commandeId
 *   POST /prepreparation/:id/valider
 *   POST /prepreparation/:id/liberer
 */
/** Résumé pilotage d'un article (tooltip « infos réf »). */
export interface ArticlePilotageSummary {
  articleId: string;
  uniteArticle: string | null;
  uniteVente: string | null;
  coefficient: number;
  besoinUG: number;
  nbCommandes: number;
  engageUG: number;
  stockDispo: number;
  stockDispoEligible: number;
  /** Éligible pour la contrainte d'une ligne (livraison + DDM mini) — null si non demandé. */
  stockDispoEligibleLigne: number | null;
  /** DDM mini (jours) réellement appliquée à `stockDispoEligibleLigne` (override ou cascade contractuelle). */
  ddmMiniApplique: number | null;
  /** Détail des commandes ouvertes contenant l'article (qty UG par CC, tri date effective ASC). */
  commandes: ArticlePilotageSummaryCommande[];
}

export interface ArticlePilotageSummaryCommande {
  id: string;
  numero: string;
  tiers: string | null;
  quantiteUg: number;
  dateEffective: string | null;
  /** Date de livraison prévue (cascade ligne → CC), YYYY-MM-DD — clé de tri. */
  dateLivraison: string | null;
}

@Injectable({ providedIn: "root" })
export class PrepreparationService {
  private http = inject(HttpClient);
  private snack = inject(SnackService);

  /** Pré-prépas chargées pour la commande courante. */
  preprepas = signal<PrepreparationLigne[]>([]);
  loading = signal(false);
  autoAffecting = signal(false);
  validating = signal<string | null>(null);
  releasing = signal<string | null>(null);

  /** Charge toutes les pré-prépas d'une commande client. */
  loadForCommande(commandeId: string): Observable<PrepreparationLigne[]> {
    return this.http.get<PrepreparationLigne[]>(
      `${environment.api()}/prepreparation/commande/${commandeId}`,
    );
  }

  /**
   * Résumé pilotage d'un article (tooltip « infos réf »). Cache mémoire à
   * TTL court (60 s) par clé (article, livraison, ddmMini, tiers) : un même
   * article ne déclenche qu'un appel, mais les chiffres ne restent jamais
   * figés toute la session. `invalidateArticleSummary` est en plus appelé
   * après chaque mutation prépa (réserver / libérer / affecter / ajuster).
   */
  private _summaryCache = new Map<
    string,
    { promise: Promise<ArticlePilotageSummary>; at: number }
  >();
  private static readonly SUMMARY_TTL_MS = 60_000;
  getArticleSummary(
    articleId: string,
    opts?: {
      livraison?: string | null;
      ddmMini?: number | null;
      tiersId?: string | null;
    },
  ): Promise<ArticlePilotageSummary> {
    const liv = opts?.livraison || "";
    const ddm = opts?.ddmMini != null ? String(opts.ddmMini) : "";
    const tiers = opts?.tiersId || "";
    const key = `${articleId}|${liv}|${ddm}|${tiers}`;
    const cached = this._summaryCache.get(key);
    const fresh =
      cached && Date.now() - cached.at < PrepreparationService.SUMMARY_TTL_MS;
    if (!fresh) {
      let url = `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/summary`;
      const qs: string[] = [];
      if (liv) qs.push(`livraison=${encodeURIComponent(liv)}`);
      if (ddm) qs.push(`ddmMini=${encodeURIComponent(ddm)}`);
      if (tiers) qs.push(`tiersId=${encodeURIComponent(tiers)}`);
      if (qs.length) url += `?${qs.join("&")}`;
      this._summaryCache.set(key, {
        at: Date.now(),
        promise: firstValueFrom(
          this.http.get<ArticlePilotageSummary>(url),
        ).catch((e) => {
          this._summaryCache.delete(key); // retry au prochain survol
          throw e;
        }),
      });
    }
    return this._summaryCache.get(key)!.promise;
  }
  invalidateArticleSummary(): void {
    this._summaryCache.clear();
  }

  async fetchForCommande(commandeId: string): Promise<void> {
    this.loading.set(true);
    try {
      const result = await firstValueFrom(this.loadForCommande(commandeId));
      this.preprepas.set(result ?? []);
    } finally {
      this.loading.set(false);
    }
  }

  /** Pré-prépa d'une ligne (ou null). */
  loadForLigne(
    commandeClientLigneId: string,
  ): Observable<PrepreparationLigne | null> {
    return this.http.get<PrepreparationLigne | null>(
      `${environment.api()}/prepreparation/ligne/${commandeClientLigneId}`,
    );
  }

  /** P5d — Variante one-shot Promise (pour le dialog d'affectation manuelle). */
  async loadForLigneOnce(
    commandeClientLigneId: string,
  ): Promise<PrepreparationLigne | null> {
    return firstValueFrom(this.loadForLigne(commandeClientLigneId));
  }

  /** Auto-affecte les lots d'une ligne via l'algo glouton DDM-aware. */
  async autoAffecterLigne(
    commandeClientLigneId: string,
  ): Promise<PrepreparationLigne> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.autoAffecting.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/auto-affecter/ligne/${commandeClientLigneId}`,
          {},
        ),
      );
      this.upsertLocal(result);
      return result;
    } finally {
      this.autoAffecting.set(false);
    }
  }

  /** Auto-affecte toutes les lignes d'une commande. */
  async autoAffecterCommande(
    commandeId: string,
  ): Promise<{ ligneId: string; ok: boolean; prep?: PrepreparationLigne; error?: string; eclatementMultiEntrepots?: boolean }[]> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.autoAffecting.set(true);
    try {
      const results = await firstValueFrom(
        this.http.post<
          {
            ligneId: string;
            ok: boolean;
            prep?: PrepreparationLigne;
            error?: string;
            eclatementMultiEntrepots?: boolean;
          }[]
        >(
          `${environment.api()}/prepreparation/auto-affecter/commande/${commandeId}`,
          {},
        ),
      );
      // Recharge après auto-affectation en masse
      await this.fetchForCommande(commandeId);
      const okCount = results.filter((r) => r.ok).length;
      const errCount = results.length - okCount;
      if (errCount === 0) {
        this.snack.success(
          `${okCount} ligne${okCount > 1 ? "s" : ""} pré-préparée${okCount > 1 ? "s" : ""}`,
        );
      } else {
        this.snack.warn(
          `${okCount} ligne(s) ok, ${errCount} en erreur — voir détail`,
        );
      }
      return results;
    } finally {
      this.autoAffecting.set(false);
    }
  }

  /** Valide une pré-prépa : DRAFT → VALIDEE. */
  async valider(id: string): Promise<PrepreparationLigne> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.validating.set(id);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/${id}/valider`,
          {},
        ),
      );
      this.upsertLocal(result);
      this.snack.success("Pré-préparation validée");
      return result;
    } finally {
      this.validating.set(null);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // REFONTE PRÉPA PHYSIQUE (mai 2026) — nouveaux endpoints d'engagement
  // Cf. docs/plans/refonte-prepa-physique.md
  // ═══════════════════════════════════════════════════════════════════

  /** Signal mis à `ligneId` pendant qu'un engagement est en cours. */
  engagingLigne = signal<string | null>(null);
  /** Signal vrai pendant l'engagement global d'une CC. */
  engagingCommande = signal(false);
  /** Signal mis à `ligneId` pendant un retour en simulation. */
  reverting = signal<string | null>(null);

  /**
   * Engage une ligne : transforme le DRAFT en mouvements physiques
   * ENGAGEMENT_PREPA vers la zone-prepa-CC. Supprime le DRAFT après.
   */
  async engagerLigne(
    commandeClientLigneId: string,
    motif?: string | null,
    motifCommentaire?: string | null,
    opts?: { silent?: boolean },
  ): Promise<void> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.engagingLigne.set(commandeClientLigneId);
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.api()}/prepreparation/engager/ligne/${commandeClientLigneId}`,
          {
            motif: motif ?? null,
            motifCommentaire: motifCommentaire ?? null,
          },
          // Skip l'intercepteur d'erreur global : l'erreur est traitée de
          // manière contextuelle (message back humain) par le caller.
          { headers: { "skip-error-interceptor": "true" } },
        ),
      );
      // `silent` : utilisé par les flux bulk (engagerToutes) pour éviter
      // N snacks de succès — le caller affiche un message agrégé unique.
      if (!opts?.silent) {
        this.snack.success("Ligne réservée — stock en zone prépa");
      }
    } finally {
      this.engagingLigne.set(null);
    }
  }

  /**
   * Fusion mai 2026 (task #80) — Ajustement partiel d'un engagement
   * existant. Permet d'ajouter / augmenter / réduire la qty engagée
   * lot par lot, sans avoir à faire "Revenir en simulation" puis
   * "Engager à nouveau".
   *
   * Refus côté back si la réduction dépasse la qty vrac dispo (lot
   * partiellement palétisé peut pas descendre sous la part committée).
   */
  ajustingEngagement = signal<string | null>(null);
  async ajusterEngagement(
    commandeClientLigneId: string,
    adjustments: {
      lotId: string;
      deltaQty: number;
      motifForce?: string | null;
      motifForceCommentaire?: string | null;
    }[],
  ): Promise<void> {
    if (!adjustments || adjustments.length === 0) return;
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.ajustingEngagement.set(commandeClientLigneId);
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.api()}/prepreparation/engager/ligne/${commandeClientLigneId}/ajuster`,
          { adjustments },
        ),
      );
      const augmentes = adjustments.filter((a) => a.deltaQty > 0).length;
      const reduits = adjustments.filter((a) => a.deltaQty < 0).length;
      const parts: string[] = [];
      if (augmentes > 0) parts.push(`${augmentes} lot(s) augmenté(s)`);
      if (reduits > 0) parts.push(`${reduits} lot(s) réduit(s)`);
      this.snack.success(`Réservation ajustée — ${parts.join(", ")}`);
    } finally {
      this.ajustingEngagement.set(null);
    }
  }

  /**
   * Engage toutes les lignes éligibles d'une commande. `scope=all` =
   * best-effort (partielles OK), `scope=completesOnly` = seulement 100% (Q22).
   */
  async engagerCC(
    commandeClientId: string,
    scope: "all" | "completesOnly" = "all",
  ): Promise<{ engaged: number; skipped: number }> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.engagingCommande.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<{ engaged: number; skipped: number }>(
          `${environment.api()}/prepreparation/engager/commande/${commandeClientId}`,
          { scope },
        ),
      );
      if (result.skipped === 0) {
        this.snack.success(
          `${result.engaged} ligne${result.engaged > 1 ? "s" : ""} engagée${result.engaged > 1 ? "s" : ""}`,
        );
      } else {
        this.snack.warn(
          `${result.engaged} engagée(s), ${result.skipped} sautée(s) (incomplète(s) ou stock indispo)`,
        );
      }
      return result;
    } finally {
      this.engagingCommande.set(false);
    }
  }

  /**
   * Retour en simulation depuis l'engagement. Génère les mouvements
   * inverses et recrée un DRAFT à partir de l'état actuel. Refusé si du
   * stock est déjà parti en palette/expé (Q2).
   */
  async revenirEnSimulation(
    commandeClientLigneId: string,
  ): Promise<PrepreparationLigne> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.reverting.set(commandeClientLigneId);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/revenir-simulation/ligne/${commandeClientLigneId}`,
          {},
        ),
      );
      this.upsertLocal(result);
      this.snack.success("Retour en pré-réservation — stock libéré");
      return result;
    } finally {
      this.reverting.set(null);
    }
  }

  /**
   * Breakdown stock d'un lot pour la fiche lot enrichie (Phase 9 — Q23D).
   * 4 catégories : stockPrincipal / engagePrepa / surPalette / expedie.
   */
  async getLotBreakdown(lotId: string): Promise<{
    lotId: string;
    stockPrincipal: number;
    engagePrepa: number;
    surPalette: number;
    expedie: number;
    quantiteTotale: number;
    parZonePrepa: { commandeClientId: string | null; quantite: number }[];
    parPalette: { paletteId: string; quantite: number }[];
  }> {
    return firstValueFrom(
      this.http.get<any>(
        `${environment.api()}/prepreparation/breakdown/lot/${lotId}`,
      ),
    );
  }

  /**
   * Breakdown de progression par ligne CCL pour le composant
   * `vl-progress-prepa` unifié. Retourne 5 segments par ligne.
   */
  async getBreakdownByCC(commandeClientId: string): Promise<
    {
      ligneId: string;
      articleId: string;
      articleDenomination: string | null;
      uniteArticle: string | null;
      /**
       * 2026-06-01 — Type de date limite (DDM / DLC / AUCUNE / MIXTE)
       * pour libeller dynamiquement "DDM mini" / "DLC mini" dans les
       * vues prépa.
       */
      typeDateLimite: "DDM" | "DLC" | "AUCUNE" | "MIXTE" | null;
      quantite: number;
      pasDemarre: number;
      enSimulation: number;
      engage: number;
      enPalette: number;
      expedie: number;
      ddmMini: {
        jours: number | null;
        niveauSource: "TIER" | "GROUPE" | "GENERIQUE" | null;
        niveauLabel: string;
      };
    }[]
  > {
    return firstValueFrom(
      this.http.get<any[]>(
        `${environment.api()}/prepreparation/breakdown/commande/${commandeClientId}`,
      ),
    );
  }

  /**
   * Refonte entrepôts CC (mai 2026) — liste des entrepôts ayant au
   * moins un stock actif engagé en zone-prepa-CC. Utilisé côté aside
   * pour étendre `entrepotsDerivedFromUE`.
   */
  async getEntrepotsEngagesForCC(
    commandeClientId: string,
  ): Promise<{ id: string; denomination: string }[]> {
    return firstValueFrom(
      this.http.get<{ id: string; denomination: string }[]>(
        `${environment.api()}/prepreparation/commande/${commandeClientId}/entrepots-engages`,
      ),
    );
  }

  /** Libère une pré-prépa : passage en OBSOLETE. */
  async liberer(id: string): Promise<PrepreparationLigne> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.releasing.set(id);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/${id}/liberer`,
          {},
        ),
      );
      this.upsertLocal(result);
      this.snack.success("Pré-préparation libérée");
      return result;
    } finally {
      this.releasing.set(null);
    }
  }

  /**
   * P5a — Valide en cascade toutes les pré-prépas DRAFT d'une commande.
   * Recharge automatiquement la liste après l'opération.
   */
  async validerToutesDraft(commandeId: string): Promise<{
    ok: number;
    errors: string[];
  }> {
    this.autoAffecting.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<{ ok: number; errors: string[] }>(
          `${environment.api()}/prepreparation/commande/${commandeId}/valider-toutes`,
          {},
        ),
      );
      await this.fetchForCommande(commandeId);
      if (result.errors.length === 0) {
        this.snack.success(
          `${result.ok} pré-préparation${result.ok > 1 ? "s" : ""} validée${result.ok > 1 ? "s" : ""}`,
        );
      } else {
        this.snack.warn(
          `${result.ok} validée(s), ${result.errors.length} en erreur`,
        );
      }
      return result;
    } finally {
      this.autoAffecting.set(false);
    }
  }

  /** P5a — Libère en cascade toutes les pré-prépas non-OBSOLETE d'une commande. */
  async libererToutes(commandeId: string): Promise<{ liberees: number }> {
    this.autoAffecting.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<{ liberees: number }>(
          `${environment.api()}/prepreparation/commande/${commandeId}/liberer-toutes`,
          {},
        ),
      );
      await this.fetchForCommande(commandeId);
      this.snack.success(
        `${result.liberees} pré-préparation${result.liberees > 1 ? "s" : ""} libérée${result.liberees > 1 ? "s" : ""}`,
      );
      return result;
    } finally {
      this.autoAffecting.set(false);
    }
  }

  /**
   * P5b — Marque une fraction (ou la totalité) d'un lot pré-préparé comme
   * consommée (placée physiquement en palette).
   */
  consuming = signal<string | null>(null);

  /** P5c — Données de la vue de pilotage cross-commandes. */
  pilotage = signal<PilotageCommandeRow[]>([]);
  /** P5c — Pivot par produit. */
  pilotageProduit = signal<PilotageProduitRow[]>([]);
  loadingPilotage = signal(false);

  async loadPilotage(horizonJours = 14): Promise<void> {
    this.loadingPilotage.set(true);
    try {
      const result = await firstValueFrom(
        this.http.get<PilotageCommandeRow[]>(
          `${environment.api()}/prepreparation/pilotage?horizon=${horizonJours}`,
        ),
      );
      this.pilotage.set(result ?? []);
    } finally {
      this.loadingPilotage.set(false);
    }
  }

  async loadPilotageProduit(horizonJours = 14): Promise<void> {
    this.loadingPilotage.set(true);
    try {
      const result = await firstValueFrom(
        this.http.get<PilotageProduitRow[]>(
          `${environment.api()}/prepreparation/pilotage/by-produit?horizon=${horizonJours}`,
        ),
      );
      this.pilotageProduit.set(result ?? []);
    } finally {
      this.loadingPilotage.set(false);
    }
  }

  /**
   * Pilotage par Produit — récupère la grille pivot lots × CC pour
   * arbitrage cross-commandes (refonte mai 2026).
   */
  async loadProduitGrid(
    articleId: string,
    horizonJours = 30,
  ): Promise<PilotageProduitGridResponse> {
    return firstValueFrom(
      this.http.get<PilotageProduitGridResponse>(
        `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/grid?horizon=${horizonJours}`,
      ),
    );
  }

  /**
   * Forecast stock prévisionnel pour un produit sur N jours.
   * Retourne stockInitial + events (CC / OF / réceptions) + dataPoints.
   */
  async loadProduitForecast(
    articleId: string,
    horizonJours = 60,
  ): Promise<StockForecastResponse> {
    return firstValueFrom(
      this.http.get<StockForecastResponse>(
        `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/forecast?horizon=${horizonJours}`,
      ),
    );
  }

  /**
   * Pilotage par Produit — bulk update des cellules de la grille.
   * Idempotent : remplace les affectations de chaque ligne par celles
   * fournies.
   */
  async affecterGridProduit(
    articleId: string,
    affectations: {
      ligneId: string;
      lotId: string;
      quantite: number;
      motifForce?: string | null;
      motifForceCommentaire?: string | null;
    }[],
    partial = false,
  ): Promise<{ updatedLignes: number }> {
    const qs = partial ? '?partial=true' : '';
    return firstValueFrom(
      this.http.post<{ updatedLignes: number }>(
        `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/affecter-grid${qs}`,
        { affectations },
      ),
    );
  }

  /**
   * Pilotage par Produit — algo auto global cross-CC paramétrable.
   */
  async autoAffecterGridProduit(
    articleId: string,
    strategy: 'hybrid' | 'ddm-desc' | 'livraison-asc' = 'hybrid',
    horizonJours = 30,
    wholeUvOnly = true,
    bypassEntrepots = false,
  ): Promise<PilotageProduitGridResponse> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    const url =
      `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/auto-global` +
      `?strategy=${strategy}&horizon=${horizonJours}&wholeUvOnly=${wholeUvOnly}` +
      `&bypassEntrepots=${bypassEntrepots}`;
    return firstValueFrom(
      this.http.post<PilotageProduitGridResponse>(url, {}),
    );
  }

  /** P-suite-4 — Génère et imprime le PDF "fiche de picking" d'une commande. */
  pickingPdfLoading = signal(false);
  async printFichePicking(
    commandeClientId: string,
    titre = "fiche-picking",
  ): Promise<void> {
    this.pickingPdfLoading.set(true);
    try {
      const res = await firstValueFrom(
        this.http.get(
          `${environment.api()}/prepreparation/picking/pdf/${commandeClientId}`,
          { responseType: "blob" },
        ),
      );
      const blob = new Blob([res], { type: "application/pdf" });
      printPdf(URL.createObjectURL(blob), titre);
    } catch (e) {
      this.snack.error(
        "Échec de la génération de la fiche de picking",
      );
    } finally {
      this.pickingPdfLoading.set(false);
    }
  }

  /** P5d — Réservations pré-prépa pour un lot (page lot). */
  async loadReservationsSummaryPourLot(
    lotId: string,
  ): Promise<LotReservationsSummary> {
    return firstValueFrom(
      this.http.get<LotReservationsSummary>(
        `${environment.api()}/prepreparation/lot/${lotId}/reservations-summary`,
      ),
    );
  }

  /** P5d — Drilldown : stocks par lot d'un article (vue dispo réel). */
  async loadStocksParLotPourArticle(
    articleId: string,
  ): Promise<LotStockRow[]> {
    return firstValueFrom(
      this.http.get<LotStockRow[]>(
        `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/stocks`,
      ),
    );
  }

  /** P5d — Drilldown : commandes consommatrices d'un article (sur l'horizon). */
  async loadCommandesPourArticle(
    articleId: string,
    horizonJours = 14,
  ): Promise<PilotageProduitDrilldownRow[]> {
    return firstValueFrom(
      this.http.get<PilotageProduitDrilldownRow[]>(
        `${environment.api()}/prepreparation/pilotage/by-produit/${articleId}/commandes?horizon=${horizonJours}`,
      ),
    );
  }

  /** P5d — Liste les pré-prépas d'une commande (pour drilldown card commande). */
  async loadPrepasForCommande(commandeId: string): Promise<PrepreparationLigne[]> {
    return firstValueFrom(this.loadForCommande(commandeId));
  }

  /** P5d — Liste les lignes d'une commande avec article + statut prep. */
  async loadLignesPourCommande(
    commandeId: string,
  ): Promise<PilotageCommandeDrilldownRow[]> {
    return firstValueFrom(
      this.http.get<PilotageCommandeDrilldownRow[]>(
        `${environment.api()}/prepreparation/pilotage/by-commande/${commandeId}/lignes`,
      ),
    );
  }

  /** P5d — Liste lots dispos pour une ligne (DDM-aware) — picker manuel. */
  async loadLotsDisponiblesPourLigne(
    ligneId: string,
    opts: {
      includeAllStatuses?: boolean;
      scope?: "tous" | "commande";
    } = {},
  ): Promise<LotDisponibleRow[]> {
    const params: string[] = [];
    if (opts.includeAllStatuses) params.push("includeAllStatuses=true");
    if (opts.scope) params.push(`scope=${opts.scope}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    const url = `${environment.api()}/prepreparation/ligne/${ligneId}/lots-disponibles${qs}`;
    return firstValueFrom(this.http.get<LotDisponibleRow[]>(url));
  }

  /** P5d — Affectation manuelle de lots à une ligne. */
  affectingManuel = signal(false);

  async affecterManuel(
    ligneId: string,
    lots: { lotId: string; quantite: number }[],
    motifForce?: string | null,
    motifForceCommentaire?: string | null,
  ): Promise<PrepreparationLigne> {
    // Les chiffres du tooltip « infos réf » changent avec cette mutation.
    this.invalidateArticleSummary();
    this.affectingManuel.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/affecter-manuel/ligne/${ligneId}`,
          {
            lots,
            ...(motifForce ? { motifForce } : {}),
            ...(motifForceCommentaire
              ? { motifForceCommentaire }
              : {}),
          },
        ),
      );
      this.upsertLocal(result);
      this.snack.success(
        result.aRevoir
          ? "Lots affectés — couverture partielle"
          : "Lots affectés",
      );
      return result;
    } finally {
      this.affectingManuel.set(false);
    }
  }

  async consommerLot(
    prepLotId: string,
    quantite: number,
  ): Promise<PrepreparationLigne> {
    this.consuming.set(prepLotId);
    try {
      const result = await firstValueFrom(
        this.http.post<PrepreparationLigne>(
          `${environment.api()}/prepreparation/lot/${prepLotId}/consommer`,
          { quantite },
        ),
      );
      this.upsertLocal(result);
      this.snack.success("Lot marqué consommé");
      return result;
    } finally {
      this.consuming.set(null);
    }
  }

  private upsertLocal(prep: PrepreparationLigne): void {
    const list = [...this.preprepas()];
    const idx = list.findIndex((p) => p.id === prep.id);
    if (idx >= 0) list[idx] = prep;
    else list.push(prep);
    this.preprepas.set(list);
  }
}
